import { Dispatch, SetStateAction, useCallback, useMemo } from "react";

import { Collapse, MenuItem } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/DeleteOutlined";

import { Label } from "../../components/form/Label";
import { Group } from "../../components/form/Group";
import { AlternativeHint } from "../../components/form/Hint";
import { TextField } from "../../components/form/InputField";
import { CreateRegistrationDialogViewModel, CreateRegistrationDialogErrorModel } from "./CreateRegistrationDialog";
import { useTranslation } from "../../internationalisation/translation.hook";
import { Dropdown } from "../../components/form";
import { Task } from "../registration.model";
import { DialogSection } from "../../components/dialog";
import { DialogSectionHeader } from "../../components/dialog/DialogSectionHeader";
import { GreenIconButton } from "../../components/icon-button/GreenIconButton";

export interface TaskSectionProps {
    viewModel: CreateRegistrationDialogViewModel;
    setViewModel: Dispatch<SetStateAction<CreateRegistrationDialogViewModel>>;
    errorModel: CreateRegistrationDialogErrorModel;
    validationAnimationDuration: number;
    index: number;
}

export const TaskSection = (props: TaskSectionProps) => {
    const { viewModel, setViewModel, errorModel, validationAnimationDuration, index } = props;
    const { t } = useTranslation();

    const deleteTask = useCallback(() => {
        setViewModel(viewModel => {
            if ( viewModel.tasks.length === 1 ) {
                return {
                    ...viewModel,
                    tasks: [{
                        description: "",
                        descriptionDirty: false,
                        duration: undefined,
                        durationDirty: false,
                        tools: undefined,
                        toolsDirty: false,
                    }],
                };
            }

            const tasks = [...viewModel.tasks];
            tasks.splice(index, 1);

            return { ...viewModel, tasks: tasks };
        });
    }, [setViewModel, index]);

    const changeTask = useCallback((task: Partial<CreateRegistrationDialogViewModel["tasks"][0]>) => {
        setViewModel(viewModel => ({
            ...viewModel,
            tasks: viewModel.tasks.map((x, i) => {
                if ( i !== index ) return x;

                return { ...x, ...task };
            }),
        }));
    }, [setViewModel, index]);

    const changeDescription = useCallback((value: string) => {
        changeTask({ description: value });
    }, [changeTask])

    const markDescriptionDirty = useCallback(() => {
        changeTask({ descriptionDirty: true });
    }, [changeTask]);

    const changeDuration = useCallback((value: number) => {
        changeTask({ duration: value });
    }, [changeTask])

    const markDurationDirty = useCallback(() => {
        changeTask({ durationDirty: true });
    }, [changeTask]);

    const renderDurationPlaceholder = useCallback(() => t("CreateRegistrationDialog: task-duration-placeholder"), [t]);

    const durationOptions = useMemo(() => {
        const options = [];

        const numberOfHours = 24;
        for (let i = 1; i <= 4 * numberOfHours; i++) {
            const value = i * 15;

            const hours = Math.floor(i / 4).toString().padStart(1, "0");
            const minutes = ((i % 4) * 15).toString().padStart(2, "0");

            const label = `${hours}t ${minutes}m`;

            options.push({ value, label });
        }

        return options;
    }, []);

    const changeTools = useCallback((value: Task["tools"]) => {
        changeTask({ tools: value });
    }, [changeTask])

    const markToolsDirty = useCallback(() => {
        changeTask({ toolsDirty: true });
    }, [changeTask]);

    const renderToolsPlaceholder = useCallback(() => t("CreateRegistrationDialog: task-tools-placeholder"), [t]);

    const toolsOptions = useMemo(() => {
        const options: Array<{ value: Task["tools"], label: string }> = [
            { value: "customers-tools", label: t("CreateRegistrationDialog: task-tools-customer-tools-option") },
            { value: "helper-tools", label: t("CreateRegistrationDialog: task-tools-gardener-tools-option") },
            { value: "large-tools", label: t("CreateRegistrationDialog: task-tools-gardener-large-tools-option") },
        ];

        return options;
    }, [t]);

    return (
        <DialogSection>

            <DialogSectionHeader text={`Opgave ${index + 1}`} button={<GreenIconButton icon={<DeleteIcon />} onClick={deleteTask} />} />

            <div id={`task-${index}-description-section`}>
                <Label htmlFor={`task-${index}-description`} style={{ marginBottom: "8px" }}>{t("CreateRegistrationDialog: task-description-label")}</Label>
                <Group error={Boolean(viewModel.tasks[index].descriptionDirty && errorModel.tasks[index].description)}>
                    <TextField id={`task-${index}-description`} value={viewModel.tasks[index].description} onChange={changeDescription} onBlur={markDescriptionDirty} />

                    <Collapse in={Boolean(viewModel.tasks[index].descriptionDirty && errorModel.tasks[index].description)} timeout={validationAnimationDuration}>
                        <AlternativeHint message={errorModel.tasks[index].description} />
                    </Collapse>
                </Group>
            </div>

            <div id={`task-${index}-duration-section`}>
                <Label htmlFor={`task-${index}-duration`} style={{ marginBottom: "8px" }}>{t("CreateRegistrationDialog: task-duration-label")}</Label>
                <Group error={Boolean(viewModel.tasks[index].durationDirty && errorModel.tasks[index].duration)}>
                    <Dropdown labelId={`task-${index}-duration`} variant="outlined" value={viewModel.tasks[index].duration ?? ""} onChange={changeDuration} onBlur={markDurationDirty} displayEmpty renderValue={viewModel.tasks[index].duration ? undefined : renderDurationPlaceholder}>
                        {durationOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                    </Dropdown>

                    <Collapse in={Boolean(viewModel.tasks[index].durationDirty && errorModel.tasks[index].duration)} timeout={validationAnimationDuration}>
                        <AlternativeHint message={errorModel.tasks[index].duration} />
                    </Collapse>
                </Group>
            </div>

            <div id={`task-${index}-tools-section`}>
                <Label htmlFor={`task-${index}-tools`} style={{ marginBottom: "8px" }}>{t("CreateRegistrationDialog: task-tools-label")}</Label>
                <Group error={Boolean(viewModel.tasks[index].toolsDirty && errorModel.tasks[index].tools)}>
                    <Dropdown labelId={`task-${index}-tools`} variant="outlined" value={viewModel.tasks[index].tools ?? ""} onChange={changeTools} onBlur={markToolsDirty} displayEmpty renderValue={viewModel.tasks[index].tools ? undefined : renderToolsPlaceholder}>
                        {toolsOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                    </Dropdown>

                    <Collapse in={Boolean(viewModel.tasks[index].toolsDirty && errorModel.tasks[index].tools)} timeout={validationAnimationDuration}>
                        <AlternativeHint severity={errorModel.tasks[index].tools?.severity} message={errorModel.tasks[index].tools?.message} />
                    </Collapse>
                </Group>
            </div>

        </DialogSection>
    );
};
